import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  useGetAgenticWmUsageQuery,
  useListAgenticWmAllThreadsQuery,
  useLazyGetAgenticWmReviewThreadQuery,
  useListAgenticWmFeedbackQuery,
  useListAgenticWmViewsQuery,
} from '../app/api';
import { Message } from './work/AgenticWm';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
  muted:     '#6b7280',
};

const fmtWhen = (d) => (d ? new Date(`${String(d).replace(' ', 'T')}Z`).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '-');
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const who = (email) => (email || '').split('@')[0] || '-';

const card = { background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', marginBottom: '1.25rem' };
const th = { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.muted, fontWeight: 700, whiteSpace: 'nowrap' };
const td = { fontSize: '0.85rem', verticalAlign: 'middle' };

function Section({ icon, title, right, children }) {
  return (
    <div style={card}>
      <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.7rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
        <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
        {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
      </div>
      <div style={{ padding: '0.5rem 0.75rem' }}>{children}</div>
    </div>
  );
}
Section.propTypes = { icon: PropTypes.string, title: PropTypes.string, right: PropTypes.node, children: PropTypes.node };

const Thumbs = ({ up, down }) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    <span style={{ color: up ? brand.green : brand.muted }}><i className="bi bi-hand-thumbs-up me-1" />{up || 0}</span>
    <span style={{ color: down ? brand.red : brand.muted, marginLeft: 10 }}><i className="bi bi-hand-thumbs-down me-1" />{down || 0}</span>
  </span>
);
Thumbs.propTypes = { up: PropTypes.number, down: PropTypes.number };

function Usage() {
  const [days, setDays] = useState(30);
  const { data, isFetching } = useGetAgenticWmUsageQuery(days);
  const t = data?.totals;
  const tiles = t ? [
    ['Testers', t.testers], ['Questions', t.questions], ['Web searches', t.searches],
    ['Helpful / not', <Thumbs key="t" up={t.thumbs_up} down={t.thumbs_down} />],
    ['Failed replies', t.failed], ['Est. cost', money(t.est_cost)],
  ] : [];
  return (
    <Section icon="graph-up" title="Usage" right={
      <select className="form-select form-select-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 'auto' }}>
        {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
      </select>}>
      {isFetching && !data ? <div style={{ padding: '1rem', color: brand.muted }}>Loading…</div> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', padding: '0.5rem 0.25rem 0.75rem' }}>
            {tiles.map(([label, value]) => (
              <div key={label} style={{ border: '1px solid #e3ded6', borderRadius: 8, padding: '0.6rem 0.8rem' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.muted, fontWeight: 700 }}>{label}</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: brand.deepBlue }}>{value}</div>
              </div>
            ))}
          </div>
          {!data?.users?.length ? <div style={{ padding: '0.5rem 0.25rem 1rem', color: brand.muted }}>No one has used Agentic WM in this period.</div> : (
            <div className="table-responsive">
              <table className="table table-sm mb-1">
                <thead><tr>
                  {['Tester', 'Clients', 'Threads', 'Questions', 'Searches', 'Feedback', 'Failed', 'Est. cost', 'Last used'].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.owner_uuid}>
                      <td style={td} title={u.username}>{who(u.username)}</td>
                      <td style={td}>{u.clients}</td>
                      <td style={td}>{u.threads}</td>
                      <td style={td}>{u.questions}</td>
                      <td style={td}>{u.searches}</td>
                      <td style={td}><Thumbs up={u.thumbs_up} down={u.thumbs_down} /></td>
                      <td style={{ ...td, color: u.failed ? brand.red : undefined }}>{u.failed}{u.declined ? ` (${u.declined} declined)` : ''}</td>
                      <td style={td}>{money(u.est_cost)}</td>
                      <td style={td}>{fmtWhen(u.last_used)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: '0.72rem', color: brand.muted, padding: '0 0.25rem 0.5rem' }}>
            Cost is an estimate at Anthropic&apos;s list price for Claude Opus 5.5 plus AWS&apos;s per-search price; Bedrock bills its own rates. Searches are counted from 2026-10-27.
          </div>
        </>
      )}
    </Section>
  );
}

/** One conversation, read-only. Opening it is logged on the server. */
function Review({ thread, onClose }) {
  return (
    <Section icon="eye" title={`Reviewing: ${thread.title}`} right={
      <button type="button" className="btn btn-sm btn-light" onClick={onClose}><i className="bi bi-x-lg me-1" />Close</button>}>
      <div style={{ fontSize: '0.82rem', color: brand.charcoal, padding: '0.25rem 0.25rem 0.5rem' }}>
        <strong>{thread.advisor?.name || who(thread.owner)}</strong>{thread.advisor?.office ? ` (${thread.advisor.office})` : ''} about <strong>{thread.client_name}</strong>
        {' · '}CRM snapshot {thread.opened_snapshot}{thread.archived ? ' · removed from their list' : ''}
        <div style={{ color: brand.muted, fontSize: '0.75rem', marginTop: 2 }}>Read-only. This view was recorded in the review log.</div>
      </div>
      <div style={{ maxWidth: 900 }}>
        {thread.messages.map((m) => (
          <div key={m.id}>
            <Message m={m} />
            {m.role === 'assistant' && m.est_cost !== null && (
              <div style={{ fontSize: '0.7rem', color: brand.muted, marginTop: -6, marginBottom: 8, textAlign: 'right' }}>
                {fmtWhen(m.created)} · est. {money(m.est_cost)}
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}
Review.propTypes = { thread: PropTypes.object.isRequired, onClose: PropTypes.func.isRequired };

function Conversations({ onOpen, openingId }) {
  const [rated, setRated] = useState('');
  const { data, isFetching } = useListAgenticWmAllThreadsQuery(rated ? { rated } : {});
  return (
    <Section icon="chat-square-text" title="Conversations" right={
      <select className="form-select form-select-sm" value={rated} onChange={(e) => setRated(e.target.value)} style={{ width: 'auto' }}>
        <option value="">All conversations</option>
        <option value="any">With feedback</option>
        <option value="down">With a thumbs-down</option>
      </select>}>
      {isFetching && !data ? <div style={{ padding: '1rem', color: brand.muted }}>Loading…</div>
        : !data?.threads?.length ? <div style={{ padding: '1rem', color: brand.muted }}>No conversations match.</div> : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-1">
              <thead><tr>{['Tester', 'Client', 'Conversation', 'Questions', 'Feedback', 'Last activity', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {data.threads.map((t) => (
                  <tr key={t.id}>
                    <td style={td} title={t.username}>{who(t.username)}</td>
                    <td style={td}>{t.client_name}</td>
                    <td style={{ ...td, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.title}>
                      {t.title}{t.archived && <span style={{ color: brand.muted }}> (removed)</span>}
                    </td>
                    <td style={td}>{t.questions}</td>
                    <td style={td}><Thumbs up={Number(t.thumbs_up)} down={Number(t.thumbs_down)} /></td>
                    <td style={td}>{fmtWhen(t.updated)}</td>
                    <td style={td}>
                      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={openingId === t.id} onClick={() => onOpen(t.id)}>
                        {openingId === t.id ? <span className="spinner-border spinner-border-sm" /> : 'Read'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </Section>
  );
}
Conversations.propTypes = { onOpen: PropTypes.func.isRequired, openingId: PropTypes.number };

function FeedbackList({ onOpen }) {
  const { data } = useListAgenticWmFeedbackQuery();
  if (!data?.feedback?.length) {
    return <Section icon="hand-thumbs-up" title="Latest feedback"><div style={{ padding: '1rem', color: brand.muted }}>No ratings yet.</div></Section>;
  }
  return (
    <Section icon="hand-thumbs-up" title="Latest feedback">
      {data.feedback.map((f) => (
        <div key={f.id} style={{ display: 'flex', gap: '0.75rem', padding: '0.5rem 0.25rem', borderBottom: '1px solid #f0ece6', alignItems: 'baseline' }}>
          <i className={`bi bi-hand-thumbs-${f.rating > 0 ? 'up' : 'down'}-fill`} style={{ color: f.rating > 0 ? brand.green : brand.red }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.85rem', color: brand.deepBlue }}>{f.comment || <span style={{ color: brand.muted }}>No comment</span>}</div>
            <div style={{ fontSize: '0.74rem', color: brand.muted }}>{who(f.username)} · {f.client_name} · {f.title} · {fmtWhen(f.updated)}</div>
          </div>
          <button type="button" className="btn btn-sm btn-link" onClick={() => onOpen(f.thread_id)}>Read</button>
        </div>
      ))}
    </Section>
  );
}
FeedbackList.propTypes = { onOpen: PropTypes.func.isRequired };

function ReviewLog() {
  const { data } = useListAgenticWmViewsQuery();
  return (
    <Section icon="clock-history" title="Review log">
      {!data?.views?.length ? <div style={{ padding: '1rem', color: brand.muted }}>No conversations have been reviewed yet.</div> : (
        <table className="table table-sm mb-1">
          <thead><tr>{['When', 'Reviewer', 'Whose conversation', 'Client', 'Conversation'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {data.views.map((v) => (
              <tr key={v.id}>
                <td style={td}>{fmtWhen(v.viewed)}</td>
                <td style={td}>{who(v.actor_email)}</td>
                <td style={td}>{who(v.owner_username)}</td>
                <td style={td}>{v.client_name}</td>
                <td style={td}>{v.title}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

/**
 * Super Admin -> Agentic WM Activity: who is using the Agentic WM beta, what it
 * costs, what testers say about the answers, and - logged, per conversation -
 * what was actually asked and answered. SUPER_ADMIN only, on the server too.
 */
export default function AgenticWmActivity() {
  const [tab, setTab] = useState('activity');
  const [loadThread, { data: thread, isFetching: loading, originalArgs }] = useLazyGetAgenticWmReviewThreadQuery();
  const [open, setOpen] = useState(false);
  // Each Read re-fetches (and so re-logs): a review log that skips repeat
  // opens would understate how often someone's conversation was read.
  const onOpen = (id) => { setOpen(true); loadThread(id, false); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.5rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.65rem', flexShrink: 0 }}>
            <i className="bi bi-stars" style={{ color: brand.goldenrod, fontSize: '1.5rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>Beta review</p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0 }}>Agentic WM Activity</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: '4px 0 0', fontSize: '0.85rem' }}>
              Who is using the Agentic WM beta, what it costs and what testers think of the answers.
              You can read any conversation; every read is recorded in the review log.
            </p>
          </div>
        </div>
      </div>

      {open && (loading || !thread || thread.id !== originalArgs
        ? <Section icon="eye" title="Reviewing"><div style={{ padding: '1rem', color: brand.muted }}>Loading…</div></Section>
        : <Review thread={thread} onClose={() => setOpen(false)} />)}

      <ul className="nav nav-pills mb-3">
        {[['activity', 'Usage & conversations'], ['feedback', 'Feedback'], ['log', 'Review log']].map(([key, label]) => (
          <li key={key} className="nav-item">
            <button type="button" className={`nav-link ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}
                    style={tab === key ? { background: brand.tealBlue } : { color: brand.deepBlue }}>{label}</button>
          </li>
        ))}
      </ul>
      {tab === 'activity' && (<><Usage /><Conversations onOpen={onOpen} openingId={loading ? originalArgs : null} /></>)}
      {tab === 'feedback' && <FeedbackList onOpen={onOpen} />}
      {tab === 'log' && <ReviewLog />}
    </div>
  );
}
